import { useEffect, useRef, useState } from "react";
import { Send, Sparkles, X, Plus, MessageSquare, Trash2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useAuth } from "@/hooks/useAuth";
import { useChat } from "./ChatContext";
import { cn } from "@/lib/utils";

const STARTERS = [
  "What are my highest priorities?",
  "Anything due this week for me?",
  "How are we tracking to GA?",
];

/* ─────────── Inline launcher (Overview empty state) ─────────── */
export function FelixLauncher() {
  const { user } = useAuth();
  const { send, openDrawer, isStreaming } = useChat();
  const [input, setInput] = useState("");
  const firstName = user?.name?.split(" ")[0] ?? "there";

  const handleSend = (text: string) => {
    if (!text.trim()) return;
    openDrawer();
    setInput("");
    void send(text);
  };

  return (
    <div className="relative overflow-hidden rounded-2xl border border-border bg-white shadow-sm">
      {/* Aurora layer — matches the reference: a dominant rose/magenta cloud
           in the upper-center-right, a lavender blob in the bottom-left, and
           a soft pink trailing off the right. Everything else fades to white. */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            // dominant rose/magenta cloud — upper center-right
            "radial-gradient(38% 42% at 62% 28%, rgba(231,120,170,0.55) 0%, rgba(231,120,170,0.20) 45%, rgba(231,120,170,0) 75%)," +
            // secondary warm pink — right-center, connecting down
            "radial-gradient(34% 40% at 78% 48%, rgba(244,158,192,0.45) 0%, rgba(244,158,192,0) 72%)," +
            // lavender blob — bottom-left
            "radial-gradient(32% 45% at 22% 90%, rgba(196,168,240,0.50) 0%, rgba(196,168,240,0) 72%)," +
            // soft pink wash — middle-bottom, ties the two together
            "radial-gradient(45% 40% at 50% 80%, rgba(248,198,220,0.35) 0%, rgba(248,198,220,0) 75%)",
          filter: "blur(14px)",
        }}
      />
      {/* Halftone dot field — matches the Figma reference (node 330:121149):
           #A9A9A9 pin-pricks on a tight grid, masked with the #737373 →
           transparent gradient from the Figma spec (Rectangle 17964). The
           #737373 cap means dots peak around 45% visibility so the band
           reads as a soft halftone, not a solid fill. */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[68%]"
        style={{
          backgroundImage:
            "radial-gradient(circle, rgba(169,169,169,1) 0.55px, transparent 0.8px)",
          backgroundSize: "7px 7px",
          WebkitMaskImage:
            "linear-gradient(to top, rgba(115,115,115,1) 0%, rgba(115,115,115,0.55) 45%, rgba(115,115,115,0.15) 80%, transparent 100%)",
          maskImage:
            "linear-gradient(to top, rgba(115,115,115,1) 0%, rgba(115,115,115,0.55) 45%, rgba(115,115,115,0.15) 80%, transparent 100%)",
        }}
      />
      {/* Content */}
      <div className="relative px-6 py-10 sm:py-14">
        <div className="mb-10 flex items-center justify-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-ai shadow-sm">
            <Sparkles size={15} className="text-white" />
          </div>
          <h2 className="text-center text-h3 font-semibold text-foreground">
            Hey, {firstName}. How can I help today?
          </h2>
        </div>

        <div className="mx-auto flex max-w-3xl items-center gap-2 rounded-xl border border-border bg-white px-4 py-3 shadow-sm focus-within:border-ai-purple/40 focus-within:ring-1 focus-within:ring-ai-purple/20">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleSend(input); }}
            placeholder="Ask anything — status, strategy, what to do next…"
            disabled={isStreaming}
            className="flex-1 bg-transparent text-body text-foreground placeholder:text-muted-foreground focus:outline-none"
          />
          <button
            onClick={() => handleSend(input)}
            disabled={!input.trim() || isStreaming}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-ai text-white shadow-sm transition-all hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Send size={15} />
          </button>
        </div>

        <div className="mx-auto mt-4 flex max-w-3xl flex-nowrap items-center justify-center gap-2 overflow-x-auto scrollbar-thin px-1">
          {STARTERS.map((s) => (
            <button
              key={s}
              onClick={() => handleSend(s)}
              className="shrink-0 whitespace-nowrap rounded-full border border-border bg-white/80 px-3.5 py-1.5 text-caption text-foreground shadow-sm backdrop-blur-sm transition-colors hover:border-ai-purple/40 hover:bg-ai-purple/5"
            >
              {s}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─────────── Floater FAB (visible when drawer closed) ─────────── */
export function FelixFloater() {
  const { drawerOpen, openDrawer } = useChat();
  if (drawerOpen) return null;
  return (
    <button
      onClick={openDrawer}
      className="fixed bottom-6 right-6 z-30 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-ai text-white shadow-xl transition-transform hover:scale-105 animate-gradient-flow [background-size:220%_100%]"
      title="Ask AI"
      aria-label="Open AI chat"
    >
      <Sparkles size={22} />
    </button>
  );
}

/* ─────────── Right-side drawer ─────────── */
export function FelixDrawer() {
  const { user } = useAuth();
  const {
    sessions, activeId, messages, isStreaming, error, drawerOpen,
    closeDrawer, newSession, switchSession, deleteSession, send,
  } = useChat();

  const [input, setInput] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 160)}px`;
  }, [input]);

  if (!drawerOpen) return null;

  const handleSubmit = () => {
    if (!input.trim()) return;
    void send(input);
    setInput("");
  };

  const firstName = user?.name?.split(" ")[0] ?? "there";

  return (
    <aside
      className={cn(
        "fixed right-0 top-0 z-30 flex h-screen w-full flex-col border-l border-border bg-background shadow-2xl",
        "sm:w-[420px] lg:w-[460px] xl:w-[500px]",
        "animate-[slide-in-right_220ms_ease-out]"
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-ai">
            <Sparkles size={14} className="text-white" />
          </div>
          <div>
            <div className="text-caption font-semibold">Ask AI</div>
            <div className="text-micro text-muted-foreground">
              {messages.length === 0 ? "Ready when you are" : `${messages.length} message${messages.length === 1 ? "" : "s"}`}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setHistoryOpen((h) => !h)}
            className={cn(
              "rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
              historyOpen && "bg-muted text-foreground"
            )}
            title="History"
          >
            <MessageSquare size={14} />
          </button>
          <button
            onClick={newSession}
            className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            title="New chat"
          >
            <Plus size={14} />
          </button>
          <button
            onClick={closeDrawer}
            className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            title="Close"
          >
            <X size={15} />
          </button>
        </div>
      </div>

      {/* History dropdown */}
      {historyOpen && (
        <div className="max-h-[40vh] overflow-y-auto border-b border-border bg-muted/20 p-2">
          {sessions.filter((s) => s.id === activeId || s.messages.length).map((s) => (
            <div
              key={s.id}
              className={cn(
                "group flex items-center gap-2 rounded-md px-2 py-1.5 text-caption",
                s.id === activeId ? "bg-card" : "hover:bg-card/60"
              )}
            >
              <button
                className="min-w-0 flex-1 truncate text-left"
                onClick={() => { switchSession(s.id); setHistoryOpen(false); }}
              >
                <span className="font-medium text-foreground">{s.title}</span>
                <span className="ml-2 text-micro text-muted-foreground">
                  {new Date(s.updatedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                </span>
              </button>
              <button
                onClick={() => deleteSession(s.id)}
                className="opacity-0 transition-opacity group-hover:opacity-100 text-muted-foreground hover:text-destructive"
                title="Delete"
              >
                <Trash2 size={11} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Messages */}
      <div ref={listRef} className="flex-1 overflow-y-auto scrollbar-thin px-4 py-4">
        {messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-ai animate-gradient-flow [background-size:220%_100%]">
              <Sparkles size={18} className="text-white" />
            </div>
            <h3 className="text-h4 font-semibold text-foreground">Hey, {firstName}.</h3>
            <p className="mt-1 text-caption text-muted-foreground">Ask me anything about the launch.</p>
            <div className="mt-4 grid w-full max-w-sm grid-cols-1 gap-2">
              {STARTERS.map((s) => (
                <button
                  key={s}
                  onClick={() => { void send(s); }}
                  className="rounded-lg border border-border bg-card px-3 py-2 text-caption text-foreground hover:border-ai-purple/40 hover:bg-ai-purple/5"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((m, i) => (
              <div key={i} className={cn("flex gap-2.5", m.role === "user" && "flex-row-reverse")}>
                <div
                  className={cn(
                    "flex h-7 w-7 shrink-0 items-center justify-center rounded-lg shadow-sm",
                    m.role === "user" ? "bg-primary text-white" : "bg-gradient-ai"
                  )}
                >
                  {m.role === "user" ? (
                    <span className="text-micro font-semibold">{user?.name?.[0]?.toUpperCase() ?? "U"}</span>
                  ) : (
                    <Sparkles size={12} className="text-white" />
                  )}
                </div>
                <div
                  className={cn(
                    "max-w-[85%] rounded-2xl px-3.5 py-2 text-caption leading-relaxed",
                    m.role === "user" ? "rounded-tr-sm bg-primary text-white" : "rounded-tl-sm border border-border bg-muted/40 text-foreground"
                  )}
                >
                  {m.role === "assistant" ? (
                    m.content ? (
                      <div className="prose-sm max-w-none [&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-micro [&_h3]:mt-2 [&_h3]:text-body [&_h3]:font-semibold [&_li]:my-0.5 [&_p]:my-1 [&_strong]:font-semibold">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 italic text-muted-foreground">
                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-ai-purple [animation-delay:0ms]" />
                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-ai-purple [animation-delay:150ms]" />
                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-ai-purple [animation-delay:300ms]" />
                        Thinking…
                      </span>
                    )
                  ) : (
                    <span className="whitespace-pre-wrap">{m.content}</span>
                  )}
                </div>
              </div>
            ))}
            {error && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-micro text-destructive">
                {error}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Input */}
      <div className="border-t border-border bg-background p-3">
        <div className="flex items-end gap-2 rounded-xl border border-border bg-background px-3 py-2 focus-within:border-ai-purple/40 focus-within:ring-1 focus-within:ring-ai-purple/20">
          <textarea
            ref={taRef}
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmit(); }
            }}
            placeholder="Ask anything…"
            disabled={isStreaming}
            className="max-h-40 flex-1 resize-none bg-transparent text-caption text-foreground placeholder:text-muted-foreground focus:outline-none"
          />
          <button
            onClick={handleSubmit}
            disabled={!input.trim() || isStreaming}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-ai text-white transition-all hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Send size={13} />
          </button>
        </div>
        <p className="mt-1 text-center text-micro text-muted-foreground">Enter to send · Shift+Enter for new line</p>
      </div>
    </aside>
  );
}
